import { Button } from '@1trading/react/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Loading } from '@1trading/react/components/ui/loading';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { useToast } from '@1trading/react/hooks/use-toast';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useFlags } from 'launchdarkly-react-client-sdk';
import type React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import type { LdFeatureFlags } from '@/config/features';
import { ClientCategorisation } from '@/constants';
import { BINARY_ANSWERS } from '@/constants/answers';
import { useProfileAgreement } from '@/hooks/queries/use-profile-agreement';
import {
  useProfileClientCategorisation,
  useProfileClientCategorisationQuestionnaire,
  useProfileClientCategorisationQuestionnaireMutation,
} from '@/hooks/queries/use-profile-client-categorisation';
import { useProfileClientCategorisationTreatment } from '@/hooks/queries/use-profile-client-categorisation-treatment';
import { useProfileVerificationInformation } from '@/hooks/queries/use-profile-verification-information';
import { useProfileVerificationInformationSupportingInformation } from '@/hooks/queries/use-profile-verification-information-supporting-information';
import { FUTURES_VERIFICATION_LEVEL_IDENTIFIERS, WORKFLOW_LEVEL_IDENTIFIER } from '@/services/api/profile';
import { AgreementKey } from '@/services/api/profile/agreement';
import { type ClientCategorisationAnswers, clientCategorisationAnswersSchema } from '@/services/api/profile/client-categorisation-questionnaire';

const FuturesClientCategorisationForm = ({ defaultValues }: { defaultValues: Partial<ClientCategorisationAnswers> }) => {
  const form = useForm<ClientCategorisationAnswers>({
    defaultValues,
    resolver: standardSchemaResolver(clientCategorisationAnswersSchema),
  });
  const { isPending, mutate } = useProfileClientCategorisationQuestionnaireMutation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  const onSubmit = (data: ClientCategorisationAnswers) => {
    mutate(
      { answers: data },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          navigate('/futures/client-categorisation/treatment', { replace: true });
        },
      },
    );
  };

  return (
    <Form {...form}>
      <form name="client-categorisation" onSubmit={form.handleSubmit(onSubmit)}>
        <PageCardContent className="pt-8 space-y-8">
          <FormField
            name="hasRelevantDerivativesTradingFrequency"
            control={form.control}
            render={({ field }) => (
              <FormItem className="space-y-4">
                <FormLabel>{t('FuturesAppropriatenessClientCategorisation.questions.derivativesTradingFrequency')}</FormLabel>
                <FormControl>
                  <RadioGroup
                    className="flex flex-row gap-4"
                    defaultValue={String(field.value)}
                    variant="cards"
                    aria-label={t('FuturesAppropriatenessClientCategorisation.questions.derivativesTradingFrequency')}
                    onValueChange={field.onChange}
                  >
                    {BINARY_ANSWERS.map((answer) => (
                      <FormItem key={answer} className="flex-1">
                        <RadioGroupItemCard variant="outline">
                          <FormControl>
                            <RadioGroupItem value={answer} />
                          </FormControl>
                          <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                        </RadioGroupItemCard>
                      </FormItem>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="hasRelevantPortfolioValue"
            control={form.control}
            render={({ field }) => (
              <FormItem className="space-y-4">
                <FormLabel>{t('FuturesAppropriatenessClientCategorisation.questions.portfolioValue')}</FormLabel>
                <FormControl>
                  <RadioGroup
                    className="flex flex-row gap-4"
                    defaultValue={String(field.value)}
                    variant="cards"
                    aria-label={t('FuturesAppropriatenessClientCategorisation.questions.portfolioValue')}
                    onValueChange={field.onChange}
                  >
                    {BINARY_ANSWERS.map((answer) => (
                      <FormItem key={answer} className="flex-1">
                        <RadioGroupItemCard variant="outline">
                          <FormControl>
                            <RadioGroupItem value={answer} />
                          </FormControl>
                          <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                        </RadioGroupItemCard>
                      </FormItem>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="hasRelevantTradingExperience"
            control={form.control}
            render={({ field }) => (
              <FormItem className="space-y-4">
                <FormLabel>{t('FuturesAppropriatenessClientCategorisation.questions.tradingExperience')}</FormLabel>
                <FormControl>
                  <RadioGroup
                    className="flex flex-row gap-4"
                    defaultValue={String(field.value)}
                    variant="cards"
                    aria-label={t('FuturesAppropriatenessClientCategorisation.questions.tradingExperience')}
                    onValueChange={field.onChange}
                  >
                    {BINARY_ANSWERS.map((answer) => (
                      <FormItem key={answer} className="flex-1">
                        <RadioGroupItemCard variant="outline">
                          <FormControl>
                            <RadioGroupItem value={answer} />
                          </FormControl>
                          <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                        </RadioGroupItemCard>
                      </FormItem>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </PageCardContent>
        <PageCardFooter>
          <Button className="relative w-full" size="lg" type="submit" variant="primary" disabled={isPending}>
            <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
            {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
          </Button>
        </PageCardFooter>
      </form>
    </Form>
  );
};
FuturesClientCategorisationForm.displayName = 'FuturesClientCategorisationForm';

const FuturesClientCategorisationContent = ({ children }: React.PropsWithChildren) => {
  const { t } = useTranslation();

  return (
    <>
      <PageHeading>
        <PageHeadingTitle>{t('FuturesAppropriatenessClientCategorisation.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('FuturesAppropriatenessClientCategorisation.description')}</PageHeadingDescription>
      </PageHeading>
      <PageCard size="lg" bordered fixedFooter>
        {children}
      </PageCard>
    </>
  );
};
FuturesClientCategorisationContent.displayName = 'FuturesClientCategorisationContent';

const FuturesClientCategorisation = () => {
  const { isLoading: isLoadingServicesAgreement, data: servicesAgreement } = useProfileAgreement(AgreementKey.SERVICES_AGREEMENT);
  const { isLoading: isLoadingVerificationInformation, data: verificationInformation } = useProfileVerificationInformation();
  const { nciPriorityFlowEnabled } = useFlags<LdFeatureFlags>();
  const isFuturesVerificationLevel = nciPriorityFlowEnabled
    ? FUTURES_VERIFICATION_LEVEL_IDENTIFIERS.some((identifier) => verificationInformation?.verification?.level?.includes(identifier))
    : !!verificationInformation?.verification?.level?.includes(WORKFLOW_LEVEL_IDENTIFIER);
  const isWorkflowVerificationLevel = !!verificationInformation?.verification?.level?.includes(WORKFLOW_LEVEL_IDENTIFIER);
  const { isLoading: isLoadingVerificationInformationSupportingInformation, isComplete: isCompleteVerificationInformationSupportingInformationQuery } =
    useProfileVerificationInformationSupportingInformation(isWorkflowVerificationLevel);
  const { isLoading: isLoadingClientCategorisationQuestionnaire, data: clientCategorisationQuestionnaire } = useProfileClientCategorisationQuestionnaire();
  const { isLoading: isLoadingClientCategorisation, data: clientCategorisation } = useProfileClientCategorisation();
  const { isLoading: isLoadingClientCategorisationTreatment, data: clientCategorisationTreatment } = useProfileClientCategorisationTreatment();
  const isCompleteVerificationInformationSupportingInformation = isWorkflowVerificationLevel ? !!isCompleteVerificationInformationSupportingInformationQuery : true;
  const isLoading =
    isLoadingServicesAgreement ||
    isLoadingVerificationInformation ||
    isLoadingVerificationInformationSupportingInformation ||
    isLoadingClientCategorisationQuestionnaire ||
    isLoadingClientCategorisation ||
    isLoadingClientCategorisationTreatment;

  if (isLoading) return <LoadingCard />;

  if (!servicesAgreement?.date) return <Navigate to="/futures/services-agreement" replace />;

  /*
   *  Note: We don't technically need to check the verification status or verification supporting information here
   *  (as they're checked in the onboarded guard), just the level. But for completeness, we'll check it anyway.
   */
  if (!verificationInformation || verificationInformation.verification.status === 'NOT_VERIFIED' || !isFuturesVerificationLevel)
    return <Navigate to="/futures/verification" replace />;

  if (!isCompleteVerificationInformationSupportingInformation) return <Navigate to="/futures/verification/supporting-information" replace />;

  if (clientCategorisation === ClientCategorisation.PROFESSIONAL) return <Navigate to="/futures/client-categorisation/treatment" replace />;

  if (clientCategorisationTreatment) return <Navigate to="/futures/appropriateness-test" replace />;

  return (
    <FuturesClientCategorisationContent>
      <FuturesClientCategorisationForm defaultValues={clientCategorisationQuestionnaire?.answers ?? {}} />
    </FuturesClientCategorisationContent>
  );
};
FuturesClientCategorisation.displayName = 'FuturesClientCategorisation';

export default FuturesClientCategorisation;
